"use client"

import { useRef, useEffect, useState, useSyncExternalStore } from "react"
import { cn } from "@/lib/utils"

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

function subscribeReducedMotion(callback: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY)
  mq.addEventListener("change", callback)
  return () => mq.removeEventListener("change", callback)
}

// Um único IntersectionObserver partilhado por todos os itens (em vez de um
// por card — o catálogo monta 12+ por página).
const visibilityCallbacks = new Map<Element, () => void>()
let sharedObserver: IntersectionObserver | null = null

function observeOnce(el: Element, onVisible: () => void) {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const cb = visibilityCallbacks.get(entry.target)
            if (cb) {
              cb()
              unobserve(entry.target)
            }
          }
        }
      },
      { threshold: 0.1 },
    )
  }
  visibilityCallbacks.set(el, onVisible)
  sharedObserver.observe(el)
}

function unobserve(el: Element) {
  visibilityCallbacks.delete(el)
  sharedObserver?.unobserve(el)
  if (visibilityCallbacks.size === 0) {
    sharedObserver?.disconnect()
    sharedObserver = null
  }
}

export function FadeInOnScroll({
  children,
  className,
  delayMs = 0,
}: {
  children: React.ReactNode
  className?: string
  /** Atraso do reveal (ms) para escalonar itens de uma lista. Ignorado em reduced-motion. */
  delayMs?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  // Derivado no render (SSR-safe: false no servidor) — sem setState em effect.
  const prefersReducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  )

  useEffect(() => {
    // Reduced-motion (ou ambiente sem IntersectionObserver): revela sem observar.
    if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
      return
    }
    const el = ref.current
    if (!el) return
    observeOnce(el, () => setVisible(true))
    return () => unobserve(el)
  }, [prefersReducedMotion])

  // Em reduced-motion o conteúdo já nasce visível e sem transição/deslize.
  const shown = prefersReducedMotion || visible

  return (
    <div
      ref={ref}
      className={cn(
        prefersReducedMotion ? "" : "transition-all duration-500",
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className,
      )}
      style={
        prefersReducedMotion || !delayMs
          ? undefined
          : { transitionDelay: `${delayMs}ms` }
      }
    >
      {children}
    </div>
  )
}
